import { useMutation } from "@apollo/react-hooks";
import { ScheduleProvider } from "context";
import _ from "lodash";
import omit from "lodash/omit";
import { useMemo, useState } from "react";
import { formatSetOrderInput, formatUpdateOrderInput } from "utils/utils";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    Text,
} from "@/ui";
import { useMeta, useNotification } from "@/hooks";
import { MUTATION_UPDATE_STORE } from "../../apollo/module-operations/store";
import styles from "./GeneralsForm.module.scss";
import curbsideStyles from "./CurbsideSchedules.module.scss";
import ScheduleForm from "./ScheduleForm";
import SchedulesFormJSON from "./SchedulesFormJSON";
import {
    PlusIcon,
    PencilIcon,
    RowActionsMenu,
    storeTableUiStyles,
} from "./shared/index";

const CurbsideSchedulesForm = ({ inputs, setInputs, loading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const [showForm, setShowForm] = useState(false);
    const [showFormJson, setShowFormJson] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [mutate] = useMutation(MUTATION_UPDATE_STORE, {
        refetchQueries: [],
        onCompleted: resp => {
            const input = _.cloneDeep(resp.updateStore);

            setInputs(input);
            handleCancel();
            setRowSelect(null);
            setShowDeleteConfirm(false);
            setIsDelete(false);

            notify(
                `Curbside Schedule Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);

            notify(
                `Error ${
                    isCreate ? "Creating" : !isDelete ? "Updating" : "Removing"
                } Curbside Schedule. If the error persists please contact support.`,
                "error",
            );
        },
    });

    const persistStoreInput = nextInputs => {
        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput(
                    omit(nextInputs, "ignore_whitespace"),
                ),
            },
        }).catch(() => {
            setInputs(formatSetOrderInput(nextInputs));
            setIsUpdating(false);
        });
    };

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
        setShowFormJson(false);
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setIsDelete(false);
        setRowSelect(null);
    };

    const confirmDelete = () => {
        const newSchedules = _.filter(
            inputs.curbside_schedules,
            (_item, index) => index !== rowSelect,
        );

        persistStoreInput({
            ...inputs,
            curbside_schedules: newSchedules,
        });
    };

    const handlerUpdatingOrCreate = array => {
        persistStoreInput({
            ...inputs,
            curbside_schedules: array,
        });
    };

    const handlerUpdatingOrCreateJSON = nextInputs => {
        persistStoreInput(nextInputs);
    };

    const curbsideSchedules = (inputs && inputs.curbside_schedules) || [];

    const rows = useMemo(
        () =>
            curbsideSchedules.map((schedule, index) => ({
                ...schedule,
                _index: index,
            })),
        [curbsideSchedules],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm || showFormJson}
                            label={row.name || `schedule ${row._index + 1}`}
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 200,
                renderCell: row => row.name || "",
            },
            {
                id: "note",
                header: "Note",
                minWidth: 200,
                renderCell: row => row.note || "",
            },
            {
                id: "stores_provider_id",
                header: "Store Provider",
                minWidth: 180,
                renderCell: row =>
                    row.stores_provider_id ? (
                        <Badge variant="primary">
                            {row.stores_provider_id}
                        </Badge>
                    ) : (
                        "—"
                    ),
            },
        ],
        [isUpdating, showForm, showFormJson],
    );

    const toolbarDisabled = isUpdating || showFormJson || showForm;

    return (
        <>
            <Modal
                description="Are you sure you want to remove this curbside schedule?"
                footer={
                    <div className={curbsideStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={closeDeleteConfirm}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) closeDeleteConfirm();
                }}
                open={showDeleteConfirm}
                title="Remove curbside schedule"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Curbside Schedules</Heading>
                            <Text tone="secondary" variant="body-md">
                                Curbside Schedules Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add",
                                    label: "Add Curbside Schedule",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: toolbarDisabled,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                                {
                                    id: "json",
                                    label: "Update Form JSON",
                                    icon: <PencilIcon />,
                                    disabled: toolbarDisabled,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowFormJson(true);
                                    },
                                },
                            ]}
                            aria-label="Curbside schedule actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                <div className={curbsideStyles.hint}>
                    <span className={curbsideStyles.hintIcon}>
                        <Icon icon="info" />
                    </span>
                    <Text
                        as="span"
                        className={curbsideStyles.hintText}
                        tone="secondary"
                        variant="body-sm"
                    >
                        Curbside uses <strong>Pickup Schedules</strong> by
                        default. Configure at least one Curbside Schedule to set
                        curbside hours.
                    </Text>
                </div>

                {showForm ? (
                    <ScheduleProvider
                        contextType="Curbside"
                        handlerUpdatingOrCreate={handlerUpdatingOrCreate}
                        isCreate={isCreate}
                        isUpdating={isUpdating}
                        schedules={inputs.curbside_schedules}
                        selectSchedule={rowSelect}
                    >
                        <ScheduleForm handleCancel={handleCancel} />
                    </ScheduleProvider>
                ) : null}

                {showFormJson ? (
                    <SchedulesFormJSON
                        context="Curbside"
                        handleCancel={handleCancel}
                        handlerUpdatingOrCreate={handlerUpdatingOrCreateJSON}
                        inputs={inputs}
                        isUpdating={isUpdating}
                        setInputs={setInputs}
                    />
                ) : null}

                <FormLayout>
                    <FormLayout.Section title="Curbside Schedule">
                        {loading ? (
                            <div className={styles.loading}>
                                <LoadingProgress
                                    indeterminate
                                    indicator="circular"
                                    size="sm"
                                    title="Loading"
                                />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        )}
                    </FormLayout.Section>
                </FormLayout>
            </Panel>
        </>
    );
};

export default CurbsideSchedulesForm;
